import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NavbarChevronIcon from '../../layout/NavbarChevronIcon';
import DetailNavbarLeft from '../../shared/DetailNavbarLeft';
import InternationalOrderIcon from '../../shared/InternationalOrderIcon';
import NavbarCloseButton from '../../shared/NavbarCloseButton';
import {
  DEFAULT_ORDER_DETAIL_FROM_PATH,
  findSiblingIndex,
  parseOrderDetailNavState,
} from './orderDetailNavState';

interface OrderDetailNavbarProps {
  orderNumber: string;
  orderName: string;
  isInternational?: boolean;
  /** When set, × calls this instead of leaving the order. */
  onClose?: () => void;
}

function OrderDetailNavbar({
  orderNumber,
  orderName,
  isInternational = false,
  onClose,
}: OrderDetailNavbarProps): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();
  const navState = parseOrderDetailNavState(location.state);

  const siblingIndex = findSiblingIndex(navState.siblingOrderIds, orderId);
  const hasSiblings = navState.siblingOrderIds.length > 1 && siblingIndex >= 0;
  const prevSiblingId =
    hasSiblings && siblingIndex > 0 ? navState.siblingOrderIds[siblingIndex - 1] : null;
  const nextSiblingId =
    hasSiblings && siblingIndex < navState.siblingOrderIds.length - 1
      ? navState.siblingOrderIds[siblingIndex + 1]
      : null;

  const handleClose = (): void => {
    if (onClose) {
      onClose();
      return;
    }
    navigate(navState.fromPath || DEFAULT_ORDER_DETAIL_FROM_PATH);
  };

  const goToSibling = (siblingId: string | number): void => {
    navigate(`/order/${siblingId}`, { replace: true, state: navState });
  };

  return (
    <nav className="order-detail-navbar" aria-label="Order details">
      <DetailNavbarLeft />
      <div className="order-detail-navbar-titles">
        <div className="order-detail-navbar-order-number">{orderNumber}</div>
        <div className="order-detail-navbar-order-name">
          {isInternational && (
            <InternationalOrderIcon className="order-detail-navbar-international-icon" />
          )}
          {orderName}
        </div>
      </div>
      <div className="order-detail-navbar-right">
        <button
          type="button"
          className="order-detail-navbar-nav-btn"
          onClick={() => prevSiblingId != null && goToSibling(prevSiblingId)}
          disabled={prevSiblingId == null}
          aria-label="Previous order"
        >
          <NavbarChevronIcon direction="left" />
        </button>
        <button
          type="button"
          className="order-detail-navbar-nav-btn"
          onClick={() => nextSiblingId != null && goToSibling(nextSiblingId)}
          disabled={nextSiblingId == null}
          aria-label="Next order"
        >
          <NavbarChevronIcon direction="right" />
        </button>
        <NavbarCloseButton onClose={handleClose} />
      </div>
    </nav>
  );
}

export default OrderDetailNavbar;
